import React, { useState } from 'react';
import moment from 'moment';
import JobDetail from './JobDetail';
import { sendApiRequest } from 'lib/api';
import notify from 'lib/notify';
import CompaniesAutoComplete from 'components/common/CompaniesAutocomplete';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

type Slot = {
  isAvailable: boolean;
  nearestAvailableDate: Date;
};

type Props = {
  slotInfo: Slot;
  jobs: any[];
};

const Featured = ({ slotInfo, jobs = [] }: Props) => {
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [location, setLocation] = useState('');

  const onPromote = () => {
    if (!selectedCompany?._id) {
      notify('Please select a company');
      return;
    }

    setIsLoading(true);

    sendApiRequest({
      path: `/jobs/promote`,
      data: {
        title,
        jobUrl: url,
        location,
        companyId: selectedCompany._id,
      },
    })
      .then(response => {
        window.open(response.paymentUrl);
      })
      .catch(e => {
        notify(e.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const renderModal = () => {
    return (
      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            background: '#dce7f1',
            pr: 6,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box component="span" sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Typography>Promote your job -</Typography>
              <Link
                href="/promote"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: '1rem', textDecoration: 'none' }}
              >
                How it works
              </Link>
            </Box>
          </Box>
        </DialogTitle>

        <IconButton
          onClick={() => setShowModal(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent style={{ background: '#dce7f1' }}>
          <TextField label="Job Title" value={title} onChange={e => setTitle(e.target.value)} fullWidth />
          <br /> <br />
          <TextField label="Job Location" value={location} onChange={e => setLocation(e.target.value)} fullWidth />
          <br /> <br />
          <TextField label="Job URL" value={url} onChange={e => setUrl(e.target.value)} fullWidth />
          <br /> <br />
          <CompaniesAutoComplete onSelect={setSelectedCompany} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              color="success"
              onClick={() => onPromote()}
              disabled={!title || !location || !url || !selectedCompany || isLoading}
            >
              {isLoading ? 'Loading...' : 'Proceed to Payment'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Box sx={{ mt: '20px' }}>
      {renderModal()}

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          textAlign: 'left',
        }}
      >
        <mark style={{ padding: '3px' }}>Featured jobs</mark>
        {slotInfo.isAvailable ? (
          <Button variant="text" sx={{ padding: '0px 8px' }} color="success" onClick={() => setShowModal(true)}>
            Promote your job
          </Button>
        ) : (
          <Chip
            sx={{
              maxWidth: { xs: '100%', sm: 'none' },
              height: 'auto',
              py: 0.5,
              '& .MuiChip-label': { whiteSpace: 'normal' },
            }}
            label={`The nearest available slot is on ${moment(slotInfo.nearestAvailableDate).format('MMMM Do YYYY')}`}
          />
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(3, minmax(0, 1fr))',
          },
          gap: { xs: 1.5, sm: 2 },
          alignItems: 'stretch',
          mt: { xs: 0.5, sm: 1 },
        }}
      >
        {jobs.map(job => {
          return (
            <JobDetail
              key={job._id}
              job={{ ...job, datePosted: job.createdAt }}
              companyInfo={{
                name: job.companyName,
                logo: job.companyLogo,
              }}
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              onCompanyNameClick={() => {}}
              kind="featured"
              featuredVariant="grid"
              page="index"
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default Featured;
